import React, { Fragment, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import Loading from '../components/Loading';
import LoadError from '../components/LoadError';
import Banner from '../components/Banner';
import ThreeColumnRank from '../components/ThreeColumnRank';
import ZtList from '../components/ZtList';
import BannerList from '../components/BannerList';
import ThreeParts from '../components/ThreeParts';
import Sale from '../components/Sale';
import Activity from '../components/Activity';
import ZtOneImg from '../components/ZtOneImg';
import ZtShowApps from '../components/ZtShowApps';
import RankListFourRow from '../components/RankListFourRow';
import { GAME_CONTENTS } from '../../../config';
import { createAxios, sortSoftsByLocalSoftInit } from '../../../utils/utils';
import generateURL from '../generateURL';
import useLog from '../hooks/useLog';
import { scrollUse } from '../../../actions/game';

const selectInstalledSofts = createSelector(
  state => state.native,
  native => native.installedSoft,
);

export default ({ code, source }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [report, track] = useLog();
  const installedSofts = useSelector(selectInstalledSofts);

  const [cards, setCards] = useState([]);
  const [hasError, setHasError] = useState(false);
  const [lock, setLock] = useState(true);

  useEffect(() => {
    if (lock || installedSofts.length <= 0) return;

    setCards(cards => {
      return cards.map(card => {
        if (card.cardType === 'three_parts') {
          card.dataList[0].apps = sortSoftsByLocalSoftInit(card.dataList[0].apps, installedSofts)
        }
        if (card.cardType === 'rank_list_four_row') {
          card.dataList[0].apps = sortSoftsByLocalSoftInit(card.dataList[0].apps, installedSofts)
        }
        return card;
      });
    });
    setLock(true);
  }, [lock, installedSofts]);

  useEffect(() => {
    setCards([]);
    setHasError(false);
    createAxios().then(axios => {
      axios
        .get(GAME_CONTENTS, {
          params: {
            pageType: 'newGame',
            code,
          },
        })
        .then(res => {
          if (res.data.status !== 0 || !res.data.data || res.data.data.length <= 0)
            throw new Error('no cards.');

          res.data.data = res.data.data
            .map(card => {
              if (card.cardType === 'game_banner') {
                if (card.dataList && card.dataList.length > 0) {
                  card.orderNum = -(res.data.data.length - card.orderNum);
                  return card;
                }
              }

              if (card.cardType === 'three_column_rank') {
                card.dataList = (card.dataList || []).filter(
                  group => group.apps && group.apps.length > 0,
                );
                if (card.dataList.length > 0) {
                  return card;
                }
              }

              if (card.cardType === 'zt_list') {
                if (
                  card.dataList &&
                  card.dataList.length > 0 &&
                  card.dataList[0].ztList &&
                  card.dataList[0].ztList.length >= 3
                ) {
                  card.dataList[0].ztList = card.dataList[0].ztList.slice(0, 3).map(o => ({
                    ...o,
                    targetType: '3',
                    pageCardContentId: '',
                  }));
                  return card;
                }
              }

              if (card.cardType === 'banner_list') {
                if (card.dataList && card.dataList.length >= 3) {
                  card.dataList = card.dataList.slice(0, 3);
                  return card;
                }
              }

              if (card.cardType === 'three_parts') {
                if (
                  card.dataList &&
                  card.dataList.length > 0 &&
                  card.dataList[0].apps &&
                  card.dataList[0].apps.length >= 4
                ) {
                  return card;
                }
              }

              if (card.cardType === 'advert_list_three_parts') {
                if (
                  card.dataList &&
                  card.dataList.length > 0 &&
                  card.dataList[0].advertList &&
                  card.dataList[0].advertList.length >= 4
                ) {
                  card.dataList[0].advertList = card.dataList[0].advertList.sort(
                    (a, b) => a.orderNum - b.orderNum,
                  );
                  return card;
                }
              }

              if (card.cardType === 'advert_three_parts') {
                if (
                  card.dataList &&
                  card.dataList.length > 0 &&
                  card.dataList[0].advertList &&
                  card.dataList[0].advertList.length >= 4
                ) {
                  card.dataList[0].advertList = card.dataList[0].advertList.sort(
                    (a, b) => a.orderNum - b.orderNum,
                  );
                  return card;
                }
              }

              if (card.cardType === 'activity_banner') {
                if (card.dataList && card.dataList.length > 0) {
                  return card;
                }
              }

              if (card.cardType === 'zt_one_img') {
                if (card.dataList && card.dataList.length > 0) {
                  return card;
                }
              }

              if (card.cardType === 'zt_show_apps') {
                if (card.dataList && card.dataList.length > 0) {
                  return card;
                }
              }

              if (card.cardType === 'rank_list_four_row') {
                if (
                  card.dataList &&
                  card.dataList.length > 0 &&
                  card.dataList[0].apps &&
                  card.dataList[0].apps.length >= 12
                ) {
                  return card;
                }
              }

              return null;
            })
            .filter(card => !!card);

          if (code === 'gameActivity') {
            setCards(res.data.data);
          } else {
            setCards(res.data.data.sort((a, b) => a.orderNum - b.orderNum));
          }

          setLock(false);

          dispatch(scrollUse(history.action));

          report('P', source, { source });
        })
        .catch(() => {
          setHasError(true);
        });
    });
  }, [code]);

  useEffect(() => {
    track(0, source);
  }, [code]);

  useEffect(() => {
    let _StartTime = Date.now();
    return () => {
      report('I', 'View_Time', {
        time: Math.ceil((Date.now() - _StartTime) / 1000),
        source,
      });
    };
  }, [code]);

  return (
    <Fragment>
      {cards.length > 0 && <div style={{ height: '16px' }} />}
      {cards.length > 0 &&
        cards.map((card, index) => {
          let tag = source;
          if (card.cardType === 'game_banner') {
            return (
              <Banner
                key={index}
                autoplay
                gesture
                type="fade"
                tag={`${tag}_Banner`}
                data={card.dataList}
              />
            );
          }

          if (card.cardType === 'three_column_rank') {
            return (
              <ThreeColumnRank
                key={index}
                tag={`${tag}_Rank`}
                ranks={card.dataList} />
            );
          }

          if (card.cardType === 'zt_list') {
            return (
              <ZtList
                key={index}
                title={card.title}
                operate={card.operate}
                to={generateURL(card)}
                tag={`${tag}_ZtList||${card.dataList[0].code || ''}`}
                data={card.dataList[0].ztList}
              />
            );
          }

          if (card.cardType === 'banner_list') {
            return (
              <BannerList
                key={index}
                title={card.title}
                operate={card.operate}
                to={generateURL(card)}
                tag={`${tag}_ZtList`}
                data={card.dataList}
              />
            );
          }

          if (card.cardType === 'three_parts') {
            return (
              <ThreeParts
                key={index}
                title={card.title}
                operate={card.operate}
                to={generateURL(card)}
                tag={`${tag}_Group||${card.dataList[0].pageCardContentId || ''}`}
                data={card.dataList[0].apps}
              />
            );
          }

          if (card.cardType === 'advert_list_three_parts') {
            return (
              <Sale
                key={index}
                title={card.title}
                operate={card.operate}
                to={generateURL(card)}
                tag={`${tag}_HotSell||${card.dataList[0].code || ''}`}
                data={card.dataList[0].advertList}
              />
            );
          }

          if (card.cardType === 'advert_three_parts') {
            return (
              <Sale
                key={index}
                title={card.title}
                operate={card.operate}
                to={generateURL(card)}
                tag={`${tag}_HotSell||${card.dataList[0].code || ''}`}
                data={card.dataList[0].advertList}
              />
            );
          }

          if (card.cardType === 'activity_banner') {
            return (
              <Activity
                key={index}
                tag={tag}
                data={card.dataList[0]} />
            );
          }

          if (card.cardType === 'zt_one_img') {
            return (
              <ZtOneImg
                key={index}
                title={card.title}
                operate={card.operate}
                to={generateURL(card)}
                tag={`${tag}_Group_OneImg`}
                data={card.dataList[0]}
              />
            );
          }

          if (card.cardType === 'zt_show_apps') {
            return (
              <ZtShowApps
                key={index}
                title={card.title}
                operate={card.operate}
                to={generateURL(card)}
                tag={`${tag}_Group_ZtSoft||${card.dataList[0].pageCardContentId || ''}`}
                data={card.dataList[0]}
              />
            );
          }

          if (card.cardType === 'rank_list_four_row') {
            return (
              <RankListFourRow
                key={index}
                title={card.title}
                operate={card.operate}
                to={generateURL(card)}
                tag={`${tag}_Group||${card.dataList[0].pageCardContentId || ''}`}
                data={card.dataList[0].apps}
              />
            );
          }

          return null;
        })}
      {!hasError && cards.length <= 0 && <Loading />}
      {hasError && <LoadError />}
    </Fragment>
  );
};
